import {
  LayerMaterials,
} from "../../../smokepit/src/types/MaterialVolumes";

class RequestError extends Error {
  status: number = 400;

  constructor(status: number) {
    super();

    this.status = status;
  }
}

/**
 * API response type for materials volumes endpoint.
 * After JSON parsing, Date objects become strings.
 */
export interface MaterialsVolumesApiResponse {
  data?: {
    site_id: number;
    surveys: Array<{
      survey_id: number;
      survey_label: string;
      nominal_flight_date: string | null;
      layers: LayerMaterials[];
    }>;
  };
  error?: string | boolean;
  message?: string;
}

class ApiSink {
  public loading: boolean;

  constructor() {
    this.loading = false;
  }

  public getSink() {
    return fetch("/api/v1/sink", {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => {
      if (response.status >= 400) {
        throw new RequestError(response.status);
      }

      return response.json();
    });
  }

  public getLayers(surveyId: number) {
    return fetch(`/api/v1/surveys/${surveyId}/layers`, {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }

  public getAnnotations(layerId: number) {
    return fetch(`/api/v1/layers/${layerId}/annotations`, {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }

  public getAnnotatedLayers(surveyId: number) {
    return fetch(`/api/v1/surveys/${surveyId}/annotations`, {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }

  public getDownloads(surveyId: number) {
    return fetch(`/api/v1/surveys/${surveyId}/downloads`, {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }

  public getMaterialsVolumes(surveyId: number): Promise<MaterialsVolumesApiResponse> {
    return fetch(`/api/v1/surveys/${surveyId}/charts/material-volumes`, {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }

  public getTags(signal?: AbortSignal) {
    return fetch("/api/v1/tags", {
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      signal,
    }).then(async (response) => {
      if (!response.ok) {
        return { error: true, message: "Failed to fetch tags" };
      }
      return response.json();
    });
  }

  public createTag(label: string, color?: string) {
    return fetch("/api/v1/tags", {
      method: "POST",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ label, color }),
    }).then((response) => response.json());
  }

  public updateTag(id: number, updates: { label?: string; color?: string }) {
    return fetch(`/api/v1/tags/${id}`, {
      method: "PUT",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(updates),
    }).then((response) => response.json());
  }

  public deleteTag(id: number) {
    return fetch(`/api/v1/tags/${id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json, text/plain, */*",
        "Content-Type": "application/json",
      },
    }).then((response) => response.json());
  }
}

export default ApiSink;
